import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Landmark, Play, ShieldCheck, RefreshCw, X, Plus, Trash2, Eraser } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  getGovPricingPeriods, getGovPricingPeriod, runGovPricingPeriod,
  certifyGovPricingPeriod, restateGovPricingPeriod,
  deleteGovPricingPeriod, clearGovPricingPeriods,
  getCpiuReferences, addCpiuReference, getGovExclusions, addGovExclusion
} from '../../config/api';

const PROGRAMS = [
  { value: 'MDRP', label: 'Medicaid (MDRP) — AMP / Best Price / URA' },
  { value: 'PROGRAM_340B', label: '340B Ceiling (AMP − URA)' },
  { value: 'FEDERAL', label: 'Federal — Non-FAMP / FCP' },
  { value: 'MEDICARE_ASP', label: 'Medicare Part B — ASP' }
];
const PERIOD_TYPES = ['MONTHLY', 'QUARTERLY', 'ANNUAL'];
const PRICE_TYPES = ['AMP', 'BEST_PRICE', 'URA', 'CEILING_340B', 'NON_FAMP', 'FCP', 'FSS', 'ASP'];

const STATUS_STYLES = {
  DRAFT: 'bg-gray-100 text-gray-800',
  CALCULATED: 'bg-blue-100 text-blue-800',
  CERTIFIED: 'bg-green-100 text-green-800',
  SUBMITTED: 'bg-purple-100 text-purple-800',
  RESTATED: 'bg-yellow-100 text-yellow-800'
};

const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(4)}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

const GovernmentPricing = () => {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes('ADMIN');

  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showRun, setShowRun] = useState(false);
  const [running, setRunning] = useState(false);
  const [cpiu, setCpiu] = useState([]);
  const [exclusions, setExclusions] = useState([]);

  const today = new Date();
  const [form, setForm] = useState({
    program: 'MDRP',
    periodType: 'QUARTERLY',
    periodStart: '',
    periodEnd: '',
    currentCpiuPeriod: ''
  });

  const loadPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getGovPricingPeriods();
      setPeriods(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load pricing periods');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRefs = useCallback(async () => {
    try {
      const [c, x] = await Promise.all([getCpiuReferences(), getGovExclusions()]);
      setCpiu(Array.isArray(c) ? c : []);
      setExclusions(Array.isArray(x) ? x : []);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadPeriods(); loadRefs(); }, [loadPeriods, loadRefs]);

  const openPeriod = async (id) => {
    try {
      const p = await getGovPricingPeriod(id);
      setSelected(p);
    } catch (e) { toast.error('Failed to load period'); }
  };

  const submitRun = async (e) => {
    e.preventDefault();
    if (!form.periodStart || !form.periodEnd) { toast.error('Set a start and end date'); return; }
    setRunning(true);
    try {
      const payload = {
        program: form.program,
        periodType: form.periodType,
        periodStart: new Date(form.periodStart).toISOString(),
        periodEnd: new Date(form.periodEnd).toISOString(),
        ...(form.currentCpiuPeriod ? { currentCpiuPeriod: form.currentCpiuPeriod } : {})
      };
      const res = await runGovPricingPeriod(payload);
      toast.success(`Period calculated — ${res?.resultCount ?? 0} results`);
      setShowRun(false);
      await loadPeriods();
      if (res?.period?.id) openPeriod(res.period.id);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to run period');
    } finally {
      setRunning(false);
    }
  };

  const certify = async (id) => {
    try { await certifyGovPricingPeriod(id); toast.success('Period certified'); await loadPeriods(); if (selected?.id === id) openPeriod(id); }
    catch (e) { toast.error(e.response?.data?.error || 'Certify failed'); }
  };
  const restate = async (id) => {
    try { const p = await restateGovPricingPeriod(id); toast.success('Restatement created'); await loadPeriods(); if (p?.id) openPeriod(p.id); }
    catch (e) { toast.error(e.response?.data?.error || 'Restate failed'); }
  };
  const removePeriod = async (p) => {
    if (!window.confirm(`Delete this ${p.program} ${p.periodType} period and its results? This cannot be undone.`)) return;
    try { await deleteGovPricingPeriod(p.id); toast.success('Period deleted'); if (selected?.id === p.id) setSelected(null); await loadPeriods(); }
    catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };
  const clearDrafts = async () => {
    if (!window.confirm('Clear all draft/calculated (uncertified) periods? Certified and submitted periods are kept.')) return;
    try { const r = await clearGovPricingPeriods(); toast.success(`Cleared ${r.cleared} period(s); ${r.remaining} kept`); await loadPeriods(); }
    catch (e) { toast.error(e.response?.data?.error || 'Clear failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Landmark className="h-6 w-6 text-primary-600 mr-2" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Government Pricing</h1>
            <p className="text-sm text-gray-500">Medicaid (AMP / Best Price / URA), 340B, Federal (Non-FAMP / FCP), and Medicare ASP</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button onClick={loadPeriods} title="Refresh" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </button>
          {isAdmin && periods.some(p => p.status === 'DRAFT' || p.status === 'CALCULATED') && (
            <button onClick={clearDrafts} title="Clear uncertified periods" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-red-50 hover:text-red-700 hover:border-red-300">
              <Eraser className="h-4 w-4 mr-2" /> Clear drafts
            </button>
          )}
          <button onClick={() => setShowRun(true)} title="Run Period" className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
            <Play className="h-4 w-4 mr-2" /> Run Period
          </button>
        </div>
      </div>

      {/* Compliance note */}
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
        Calculations follow the statutory methodology. Validate against current CMS/HRSA/VA guidance and counsel before any government submission.
      </div>

      {/* Periods table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200"><h2 className="text-lg font-medium text-gray-900">Pricing Periods</h2></div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Program', 'Type', 'Period', 'Status', 'Results', 'Actions'].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : periods.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">No pricing periods yet. Click “Run Period” to calculate one.</td></tr>
              ) : periods.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{p.program}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{p.periodType}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{fmtDate(p.periodStart)} – {fmtDate(p.periodEnd)}</td>
                  <td className="px-6 py-4"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[p.status] || 'bg-gray-100 text-gray-800'}`}>{p.status}</span></td>
                  <td className="px-6 py-4 text-sm text-gray-500">{p._count?.results ?? '—'}</td>
                  <td className="px-6 py-4 text-sm space-x-3">
                    <button onClick={() => openPeriod(p.id)} className="text-primary-600 hover:text-primary-800" title="View">View</button>
                    {isAdmin && (p.status === 'CALCULATED' || p.status === 'DRAFT') && (
                      <button onClick={() => certify(p.id)} className="text-green-600 hover:text-green-800 inline-flex items-center" title="Certify"><ShieldCheck className="h-4 w-4 mr-1" />Certify</button>
                    )}
                    {isAdmin && p.status === 'CERTIFIED' && (
                      <button onClick={() => restate(p.id)} className="text-yellow-600 hover:text-yellow-800" title="Restate">Restate</button>
                    )}
                    {isAdmin && (p.status === 'DRAFT' || p.status === 'CALCULATED') && (
                      <button onClick={() => removePeriod(p)} className="text-red-600 hover:text-red-800 inline-flex items-center" title="Delete this uncertified period"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected period results */}
      {selected && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              {selected.program} · {fmtDate(selected.periodStart)}–{fmtDate(selected.periodEnd)} · <span className="text-sm text-gray-500">{selected.status}</span>
            </h2>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600" title="Close"><X className="h-5 w-5" /></button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr>
                {['Product', 'NDC', 'Price Type', 'Value', 'Detail'].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr></thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(selected.results || []).length === 0 ? (
                  <tr><td colSpan={5} className="px-6 py-6 text-center text-sm text-gray-500">No results (no eligible sales in this window).</td></tr>
                ) : selected.results.map(r => (
                  <tr key={r.id}>
                    <td className="px-6 py-3 text-sm text-gray-900">{r.product?.name || r.productId}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{r.product?.ndc11 || '—'}</td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-700">{r.priceType}</td>
                    <td className="px-6 py-3 text-sm text-gray-900">{money(r.value)}</td>
                    <td className="px-6 py-3 text-xs text-gray-500">{r.detail ? Object.entries(r.detail).filter(([k]) => !['formulaVersion'].includes(k)).map(([k, v]) => `${k}: ${v}`).join(', ') : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reference data: CPI-U + Exclusions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CpiuPanel cpiu={cpiu} isAdmin={isAdmin} onAdd={async (p) => { try { await addCpiuReference(p); toast.success('CPI-U saved'); loadRefs(); } catch (e) { toast.error('Save failed'); } }} />
        <ExclusionPanel exclusions={exclusions} isAdmin={isAdmin} onAdd={async (p) => { try { await addGovExclusion(p); toast.success('Exclusion added'); loadRefs(); } catch (e) { toast.error('Add failed'); } }} />
      </div>

      {/* Run Period modal */}
      {showRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setShowRun(false)} />
          <form onSubmit={submitRun} className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Run Pricing Period</h3>
              <button type="button" onClick={() => setShowRun(false)} className="text-gray-400 hover:text-gray-600" title="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Program</label>
                <select value={form.program} onChange={e => setForm({ ...form, program: e.target.value })} className="w-full border-gray-300 rounded-md text-sm">
                  {PROGRAMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period Type</label>
                <select value={form.periodType} onChange={e => setForm({ ...form, periodType: e.target.value })} className="w-full border-gray-300 rounded-md text-sm">
                  {PERIOD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
                  <input type="date" value={form.periodStart} onChange={e => setForm({ ...form, periodStart: e.target.value })} className="w-full border-gray-300 rounded-md text-sm" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
                  <input type="date" value={form.periodEnd} onChange={e => setForm({ ...form, periodEnd: e.target.value })} className="w-full border-gray-300 rounded-md text-sm" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current CPI-U period <span className="text-gray-400">(optional, YYYY-MM)</span></label>
                <input type="text" placeholder="2026-06" value={form.currentCpiuPeriod} onChange={e => setForm({ ...form, currentCpiuPeriod: e.target.value })} className="w-full border-gray-300 rounded-md text-sm" />
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-2">
              <button type="button" onClick={() => setShowRun(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50" title="Cancel">Cancel</button>
              <button type="submit" disabled={running} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50" title="Run">{running ? 'Running…' : 'Run'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const CpiuPanel = ({ cpiu, isAdmin, onAdd }) => {
  const [period, setPeriod] = useState('');
  const [value, setValue] = useState('');
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200"><h2 className="text-lg font-medium text-gray-900">CPI-U Reference</h2><p className="text-xs text-gray-500">Used for the URA inflation penalty</p></div>
      <div className="px-6 py-4">
        {isAdmin && (
          <div className="flex space-x-2 mb-3">
            <input value={period} onChange={e => setPeriod(e.target.value)} placeholder="YYYY-MM" className="flex-1 border-gray-300 rounded-md text-sm" />
            <input value={value} onChange={e => setValue(e.target.value)} placeholder="CPI-U value" type="number" step="0.001" className="flex-1 border-gray-300 rounded-md text-sm" />
            <button onClick={() => { if (period && value) { onAdd({ period, cpiuValue: parseFloat(value) }); setPeriod(''); setValue(''); } }} className="inline-flex items-center px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700" title="Add CPI-U reference"><Plus className="h-4 w-4" /></button>
          </div>
        )}
        <div className="max-h-40 overflow-y-auto text-sm">
          {cpiu.length === 0 ? <p className="text-gray-500">No CPI-U values yet.</p> : cpiu.map(c => (
            <div key={c.id} className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-700">{c.period}</span><span className="text-gray-900">{Number(c.cpiuValue).toFixed(3)}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ExclusionPanel = ({ exclusions, isAdmin, onAdd }) => {
  const [priceType, setPriceType] = useState('BEST_PRICE');
  const [flag, setFlag] = useState('');
  const [action, setAction] = useState('EXCLUDE');
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200"><h2 className="text-lg font-medium text-gray-900">Exclusion Rules</h2><p className="text-xs text-gray-500">Include/exclude sales from AMP or Best Price</p></div>
      <div className="px-6 py-4">
        {isAdmin && (
          <div className="grid grid-cols-4 gap-2 mb-3">
            <select value={priceType} onChange={e => setPriceType(e.target.value)} className="border-gray-300 rounded-md text-sm">{PRICE_TYPES.map(t => <option key={t}>{t}</option>)}</select>
            <input value={flag} onChange={e => setFlag(e.target.value)} placeholder="Customer flag (optional)" className="border-gray-300 rounded-md text-sm" />
            <select value={action} onChange={e => setAction(e.target.value)} className="border-gray-300 rounded-md text-sm"><option>EXCLUDE</option><option>INCLUDE</option></select>
            <button onClick={() => onAdd({ priceType, customerFlag: flag || undefined, action })} className="inline-flex items-center justify-center px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700" title="Add exclusion rule"><Plus className="h-4 w-4" /></button>
          </div>
        )}
        <div className="max-h-40 overflow-y-auto text-sm">
          {exclusions.length === 0 ? <p className="text-gray-500">No custom exclusion rules (statutory defaults apply).</p> : exclusions.map(x => (
            <div key={x.id} className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-700">{x.priceType} · {x.classOfTrade || x.customerFlag || 'any'}</span><span className="text-gray-900">{x.action}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GovernmentPricing;
